// Canvas renderer for the sentiment heat map: d3-geo Albers projection of the
// real US states, an IDW heat field (masked to coverage, clipped to the US,
// softly blurred), state outlines, the national outline, and dashed ⤬ rings on
// inverse-sentiment regions.
//
// Interaction: click a state to select it — the map eases in to frame it and the
// state is highlighted; the parent shows that state's data. Click the ocean (or
// the breadcrumb) to deselect and ease back to the national view. There is no
// free scroll/drag: a discrete selection has one unambiguous dataset, where a
// continuous zoom between states does not.
//
// The heat raster is computed and blurred ONCE into an offscreen canvas; each
// frame is a cheap clipped drawImage + crisp vector outlines (weights divided by
// the zoom factor to hold a constant on-screen size). County/city LOD (more data
// points at higher zoom) is a later step.
import { type GeoPermissibleObjects, geoAlbersUsa, geoPath } from 'd3-geo';
import { useEffect, useRef } from 'react';

import {
  byFips,
  outerMesh,
  STATE_NAME_BY_FIPS,
  stateFipsAtLonLat,
  stateFipsFromPath,
  states,
} from './geography';
import { type HeatPoint, idwRaster } from './heatField';
import type { SentimentRegion } from './types';

interface Props {
  regions: SentimentRegion[];
  dark: boolean;
  width?: number;
  height?: number;
  selectedFips: string | null;
  onSelectState: (fips: string | null) => void;
  onHoverState?: (
    hover: { name: string; x: number; y: number; flip: boolean } | null,
  ) => void;
  // FIPS allowed by the region filter; null = no filter (all states shown).
  allowedFips: Set<string> | null;
}

const MAX_K = 8;
const EASE_MS = 440;
const easeInOut = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;

export function SentimentHeatMap({
  regions,
  dark,
  width = 1000,
  height = 620,
  selectedFips,
  onSelectState,
  onHoverState,
  allowedFips,
}: Props) {
  const ref = useRef<HTMLCanvasElement>(null);
  // Keep the latest callbacks + selection without re-running the heavy build effect.
  const onSelectRef = useRef(onSelectState);
  onSelectRef.current = onSelectState;
  const onHoverRef = useRef(onHoverState);
  onHoverRef.current = onHoverState;
  const selectedRef = useRef(selectedFips);
  selectedRef.current = selectedFips;
  const apiRef = useRef<{
    frameTo: (fips: string | null, animate: boolean) => void;
  } | null>(null);

  // Build the scene (projection, raster, listeners). Only re-runs when the data,
  // theme, or size changes — not on selection.
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return; // jsdom / no-canvas → skip (logic unit-tested separately)
    const W = width;
    const H = height;

    const projection = geoAlbersUsa().fitSize(
      [W, H],
      states as GeoPermissibleObjects,
    );
    const path = geoPath(projection, ctx);
    const centroidOf = (fips: string): [number, number] | null => {
      const f = byFips[fips];
      if (!f) return null;
      const c = path.centroid(f as GeoPermissibleObjects);
      return Number.isFinite(c[0]) ? [c[0], c[1]] : null;
    };

    const inFilter = (fips: string | null) =>
      !allowedFips || (!!fips && allowedFips.has(fips));

    const points: HeatPoint[] = [];
    for (const r of regions) {
      if (r.level !== 1 || r.sentiment == null) continue;
      const fips = stateFipsFromPath(r.path);
      if (!inFilter(fips)) continue;
      const c = fips ? centroidOf(fips) : null;
      if (c) points.push({ x: c[0], y: c[1], v: r.sentiment });
    }

    // Heat is clipped to the enabled states only, so a filtered-out region goes
    // blank (faint land) instead of being colored by a neighbor's bleed.
    const heatClip: GeoPermissibleObjects = allowedFips
      ? ({
          type: 'FeatureCollection',
          features: states.features.filter((f) =>
            allowedFips.has(String(f.id)),
          ),
        } as unknown as GeoPermissibleObjects)
      : (states as GeoPermissibleObjects);

    // Heat raster, pre-blurred once into an offscreen canvas so each frame is a
    // cheap drawImage (no per-frame IDW; deterministic blur under transform).
    const raster = idwRaster({ points, w: W, h: H, dark });
    const raw = document.createElement('canvas');
    raw.width = W;
    raw.height = H;
    raw.getContext('2d')?.putImageData(new ImageData(raster, W, H), 0, 0);
    const off = document.createElement('canvas');
    off.width = W;
    off.height = H;
    const offCtx = off.getContext('2d');
    if (offCtx) {
      offCtx.filter = 'blur(7px)';
      offCtx.drawImage(raw, 0, 0);
      offCtx.filter = 'none';
    }

    const inverseCentroids: [number, number][] = [];
    for (const r of regions) {
      if (r.level !== 1 || !r.isOpposite) continue;
      const fips = stateFipsFromPath(r.path);
      if (!inFilter(fips)) continue;
      const c = fips ? centroidOf(fips) : null;
      if (c) inverseCentroids.push(c);
    }

    // View transform in canvas-internal coords: screen = base * k + (x, y).
    const t = { k: 1, x: 0, y: 0 };
    let raf = 0;

    const render = () => {
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = dark ? '#15130e' : '#f1ede4';
      ctx.fillRect(0, 0, W, H);

      ctx.save();
      ctx.translate(t.x, t.y);
      ctx.scale(t.k, t.k);

      // faint land across the whole US (so filtered states still read as land)
      ctx.save();
      ctx.beginPath();
      path(states as GeoPermissibleObjects);
      ctx.clip();
      ctx.fillStyle = dark ? '#241f18' : '#e9e3d6';
      ctx.fillRect(0, 0, W, H);
      ctx.restore();

      // heat only within the enabled states (filtered regions stay blank)
      ctx.save();
      ctx.beginPath();
      path(heatClip);
      ctx.clip();
      ctx.drawImage(off, 0, 0);
      ctx.restore();

      // state borders + national outline (crisp vector; constant on-screen weight)
      ctx.lineWidth = 0.6 / t.k;
      ctx.strokeStyle = dark ? 'rgba(245,237,224,.14)' : 'rgba(43,43,43,.20)';
      for (const f of states.features) {
        ctx.beginPath();
        path(f as GeoPermissibleObjects);
        ctx.stroke();
      }
      ctx.lineWidth = 1.4 / t.k;
      ctx.strokeStyle = dark ? '#6b6456' : '#2b2b2b';
      ctx.beginPath();
      path(outerMesh as GeoPermissibleObjects);
      ctx.stroke();

      // when a filter is active, outline the in-filter states boldly so they pop
      // even where the amber fill is low-contrast against the paper.
      if (allowedFips) {
        ctx.lineWidth = 1.9 / t.k;
        ctx.strokeStyle = dark ? '#f4ede0' : '#1d1b16';
        for (const f of states.features) {
          if (!allowedFips.has(String(f.id))) continue;
          ctx.beginPath();
          path(f as GeoPermissibleObjects);
          ctx.stroke();
        }
      }

      // selected-state emphasis
      const sel = selectedRef.current;
      if (sel && byFips[sel]) {
        ctx.beginPath();
        path(byFips[sel] as GeoPermissibleObjects);
        ctx.fillStyle = dark ? 'rgba(244,237,224,0.10)' : 'rgba(29,27,22,0.07)';
        ctx.fill();
        ctx.lineWidth = 2 / t.k;
        ctx.strokeStyle = dark ? '#f4ede0' : '#1d1b16';
        ctx.stroke();
      }

      // inverse-sentiment rings — constant on-screen size regardless of zoom
      const ring = dark ? '#f4ede0' : '#1d1b16';
      ctx.strokeStyle = ring;
      ctx.fillStyle = ring;
      ctx.textAlign = 'center';
      ctx.font = `${14 / t.k}px Inter, sans-serif`;
      for (const c of inverseCentroids) {
        ctx.setLineDash([6 / t.k, 4 / t.k]);
        ctx.lineWidth = 1.6 / t.k;
        ctx.beginPath();
        ctx.arc(c[0], c[1], 27 / t.k, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillText('⤬', c[0], c[1] + 5 / t.k);
      }
      ctx.setLineDash([]);
      ctx.restore();
    };

    // Target transform that frames a state (or the whole nation when null).
    const targetFor = (fips: string | null) => {
      if (!fips || !byFips[fips]) return { k: 1, x: 0, y: 0 };
      const [[x0, y0], [x1, y1]] = path.bounds(
        byFips[fips] as GeoPermissibleObjects,
      );
      const w = Math.max(1, x1 - x0);
      const h = Math.max(1, y1 - y0);
      const k = Math.min(MAX_K, Math.max(1, 0.7 * Math.min(W / w, H / h)));
      const cx = (x0 + x1) / 2;
      const cy = (y0 + y1) / 2;
      return { k, x: W / 2 - cx * k, y: H / 2 - cy * k };
    };

    const frameTo = (fips: string | null, animate: boolean) => {
      cancelAnimationFrame(raf);
      const to = targetFor(fips);
      if (!animate) {
        Object.assign(t, to);
        render();
        return;
      }
      const from = { ...t };
      let startAt = 0;
      const step = (now: number) => {
        if (!startAt) startAt = now;
        const p = Math.min(1, (now - startAt) / EASE_MS);
        const e = easeInOut(p);
        t.k = from.k + (to.k - from.k) * e;
        t.x = from.x + (to.x - from.x) * e;
        t.y = from.y + (to.y - from.y) * e;
        render();
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    };

    apiRef.current = { frameTo };

    // State FIPS under a pointer event (CSS px → internal → base → lon/lat → state).
    // The canvas is displayed at width: 100%, so scale CSS px to internal px.
    const fipsAtEvent = (e: {
      offsetX: number;
      offsetY: number;
    }): string | null => {
      const s = W / (canvas.clientWidth || W);
      const bx = (e.offsetX * s - t.x) / t.k;
      const by = (e.offsetY * s - t.y) / t.k;
      return stateFipsAtLonLat(projection.invert?.([bx, by]) ?? null);
    };

    const onClick = (e: MouseEvent) => {
      const fips = fipsAtEvent(e);
      // Click the already-selected state (or the ocean) to deselect and zoom out.
      onSelectRef.current(fips && fips === selectedRef.current ? null : fips);
    };
    const onMove = (e: PointerEvent) => {
      const cb = onHoverRef.current;
      if (!cb) return;
      const fips = fipsAtEvent(e);
      const name = fips ? (STATE_NAME_BY_FIPS[fips] ?? null) : null;
      if (!name) {
        cb(null);
        return;
      }
      // Flip the label to the left of the cursor near the right edge so it
      // doesn't clip on the small east-coast states.
      const flip = e.offsetX > (canvas.clientWidth || W) - 130;
      cb({ name, x: e.offsetX, y: e.offsetY, flip });
    };
    const onLeave = () => onHoverRef.current?.(null);
    canvas.addEventListener('click', onClick);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerleave', onLeave);

    // Frame the current selection immediately (no animation) on (re)build.
    frameTo(selectedRef.current, false);

    return () => {
      cancelAnimationFrame(raf);
      canvas.removeEventListener('click', onClick);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerleave', onLeave);
      apiRef.current = null;
    };
  }, [regions, dark, width, height, allowedFips]);

  // Animate to frame the selection whenever it changes.
  useEffect(() => {
    apiRef.current?.frameTo(selectedFips, true);
  }, [selectedFips]);

  return (
    <canvas
      ref={ref}
      width={width}
      height={height}
      style={{
        display: 'block',
        width: '100%',
        height: 'auto',
        touchAction: 'none',
        cursor: 'pointer',
      }}
    />
  );
}
